import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Check, X as XIcon, CalendarOff, Trash2, Eye } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { DataTable, Tabs, OverflowMenu, fmtDate, num, plural } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, leaveTypeLabel } from './hrKit';
import {
  LeaveStatus, LeaveRequestDialog, LeaveReviewDrawer, useLeaveDecisions, fmtPeriod, leaveDays, isDeclined,
} from '../Leave/leaveKit';

export default function StaffLeave() {
  const { id } = useParams();
  const { user, isOffice } = useAuth();
  const canReview = isOffice;
  const confirm = useConfirm();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [requestOpen, setRequestOpen] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get(`/leave?staff_id=${id}`)
      .then((r) => setLeaves(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load this worker's leave. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  const { approve, decline, declineDialog } = useLeaveDecisions(load);

  const counts = useMemo(() => ({
    all: leaves.length,
    pending: leaves.filter((l) => l.status === 'pending').length,
    approved: leaves.filter((l) => l.status === 'approved').length,
    declined: leaves.filter((l) => isDeclined(l.status)).length,
  }), [leaves]);
  const filtered = useMemo(() => leaves.filter((l) => (filter === 'all' ? true : filter === 'declined' ? isDeclined(l.status) : l.status === filter)), [leaves, filter]);

  const canDeleteRow = (l) => canReview || (l.status === 'pending' && l.staff_id === user?.id);
  const deleteLeave = async (l) => {
    const ok = await confirm(`${leaveTypeLabel(l.leave_type)} leave, ${fmtPeriod(l.start_date, l.end_date)}, will be deleted. This can't be undone.`,
      { title: 'Delete leave request?', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/leave/${l.id}`);
      toast.success('Leave request deleted');
      setReviewing(null);
      setLeaves((list) => list.filter((x) => x.id !== l.id));
    } catch (e) { toast.error(e.response?.data?.message || "The request wasn't deleted. Try again."); }
  };

  const columns = [
    { key: 'type', header: 'Type', sortable: true, sortValue: (l) => leaveTypeLabel(l.leave_type), render: (l) => <span className="whitespace-nowrap font-medium text-slate-900">{leaveTypeLabel(l.leave_type)}</span> },
    { key: 'period', header: 'Period', sortable: true, sortValue: (l) => String(l.start_date || '').slice(0, 10), render: (l) => <span className="whitespace-nowrap">{fmtPeriod(l.start_date, l.end_date)}</span> },
    { key: 'days', header: 'Days', align: 'right', sortable: true, sortValue: (l) => leaveDays(l.start_date, l.end_date) || 0, render: (l) => <span className="tabular-nums">{num(leaveDays(l.start_date, l.end_date) || 0)}</span> },
    { key: 'status', header: 'Status', render: (l) => (
      <div><LeaveStatus status={l.status} />{isDeclined(l.status) && l.decline_reason && <p className="mt-1 max-w-[220px] truncate text-[12px] text-slate-500" title={l.decline_reason}>{l.decline_reason}</p>}</div>
    ) },
    { key: 'impact', header: 'Roster', render: (l) => (l.status === 'pending'
      ? (Number(l.conflict_count) > 0 ? <span className="badge-yellow whitespace-nowrap">{plural(l.conflict_count, 'shift')}</span> : <span className="text-[12.5px] text-slate-500">No shifts</span>)
      : <span className="text-slate-400">—</span>) },
    { key: 'submitted', header: 'Requested', sortable: true, defaultDir: 'desc', sortValue: (l) => l.created_at || null, render: (l) => <span className="whitespace-nowrap tabular-nums text-slate-600">{l.created_at ? fmtDate(l.created_at) : '—'}</span> },
    { key: 'notes', header: 'Notes', className: 'max-w-[220px]', render: (l) => (l.notes ? <span className="block truncate text-slate-600" title={l.notes}>{l.notes}</span> : <span className="text-slate-400">—</span>) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (l) => (
      <span className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {canReview && l.status === 'pending' && (
          <>
            <button type="button" onClick={() => approve({ ...l, staff_name: l.staff_name || (staff ? `${staff.first_name} ${staff.last_name}` : '') })} className="btn-primary btn-sm"><Check size={13} aria-hidden="true" />Approve</button>
            <button type="button" onClick={() => decline(l)} className="btn-secondary btn-sm"><XIcon size={13} aria-hidden="true" />Decline</button>
          </>
        )}
        <OverflowMenu label="More actions" items={[
          { label: 'Review details', icon: Eye, onClick: () => setReviewing(l) },
          canDeleteRow(l) && { divider: true },
          canDeleteRow(l) && { label: 'Delete request', icon: Trash2, danger: true, onClick: () => deleteLeave(l) },
        ].filter(Boolean)} />
      </span>
    ) },
  ];

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs variant="segmented" size="sm" ariaLabel="Leave status" value={filter} onChange={setFilter} tabs={[
            { id: 'all', label: 'All', count: counts.all },
            { id: 'pending', label: 'Pending', count: counts.pending },
            { id: 'approved', label: 'Approved', count: counts.approved },
            { id: 'declined', label: 'Declined', count: counts.declined },
          ]} />
          <button type="button" onClick={() => setRequestOpen(true)} className="btn-primary btn-sm ml-auto"><Plus size={14} aria-hidden="true" />Request leave</button>
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <DataTable
            label="Leave requests"
            columns={columns}
            rows={filtered}
            loading={loading}
            error={loadError}
            onRetry={load}
            stickyHeader={false}
            defaultSort={{ key: 'period', dir: 'desc' }}
            onRowClick={(l) => setReviewing(l)}
            empty={leaves.length === 0
              ? { icon: CalendarOff, title: 'No leave requests', hint: 'Leave this worker asks for in the app, or that you enter here, is listed on this tab.', action: <button type="button" onClick={() => setRequestOpen(true)} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />Request leave</button> }
              : { icon: CalendarOff, title: `No ${filter} leave`, hint: `${plural(leaves.length, 'request')} with another status.`, action: <button type="button" onClick={() => setFilter('all')} className="btn-secondary btn-sm">Show all</button> }}
          />
        </div>
      </div>

      <LeaveRequestDialog open={requestOpen} onClose={() => setRequestOpen(false)} onSaved={load} fixedStaff={staff} canPickStaff={canReview} />
      <LeaveReviewDrawer
        request={reviewing}
        allRequests={leaves}
        canReview={canReview}
        canDelete={reviewing ? canDeleteRow(reviewing) : false}
        onApprove={async (l, hours) => { if (await approve(l, hours)) setReviewing(null); }}
        onDecline={(l) => { setReviewing(null); decline(l); }}
        onDelete={deleteLeave}
        onClose={() => setReviewing(null)}
      />
      {declineDialog}
    </div>
  );
}
